import React, { useEffect, useState, useCallback, useRef } from 'react'
import * as THREE from 'three'
import { TweenMax, TimelineLite, Power1 } from 'gsap'
import { GUI } from 'dat-gui'
import * as Stats from 'stats-js'

import './styles.scss'

interface Props {
  data: []
  handleClick: () => void
}

export const Canvas = ({ data, handleClick }: Props) => {
  const isMobile = window.innerWidth < 600
  const minDist = isMobile ? 1.85 : 1.5
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [showInfoLayer, setShowInfoLayer] = useState(false)
  const [selectedPortrait, setSelectedPortrait] = useState(null)
  const lastPinedPosition = useRef(0)
  const scrollHint = useRef(null)
  let showLayer = useRef(false)
  const renderer = useRef(null)
  const camera = useRef(null)
  const scene = useRef(null)
  const selfies = useRef(
    [...Array(80)].map((p, i) => {
      return {
        id: i,
        random: 1 - Math.random() * 2,
        group: null,
        picture: null,
        position: {
          x: 0,
          y: 0,
          z: 0
        }
      }
    })
  )
  const portraits = useRef(
    data.map(o => ({
      ...o,
      random: 1 - Math.random() * 2,
      geometry: null,
      position: {
        x: 0,
        y: 0,
        z: 0
      }
    }))
  )

  let frame = 0

  const animate = () => {
    requestAnimationFrame(animate)
    let showPortrait = null

    // Animate each selfie
    selfies.current.forEach((s, i) => {
      if (s.group) {
        // Get a normalized distance between the selfie and the camera.
        // We will use this value to push the selfies away
        // if too close to the camera
        const dist = Math.abs(s.group.position.z - camera.current.position.z)
        const distClamped = dist > 3 ? 3 : dist
        const distNormalized = distClamped / 3

        // Push the seflie away if too close to the camera
        // In which direction should we push it?
        const angle = Math.atan2(s.group.position.y - 0, s.group.position.x - 0)
        s.group.position.x =
          s.position.x + Math.cos(angle) * (1 - distNormalized)
        s.group.position.y =
          s.position.y + Math.sin(angle) * (1 - distNormalized)

        // Give a floating effect to the selfies by randomly rotate and displace them
        // using sin and cos waves
        s.group.rotation.x += Math.sin(frame + s.random) * s.random * 0.002
        s.group.rotation.y += Math.sin(frame + s.random) * s.random * 0.002
        s.group.rotation.z += Math.sin(frame + s.random) * s.random * 0.002
        s.position.x += Math.cos(frame) * s.random * 0.001
        s.position.y += Math.sin(frame) * s.random * 0.001

        // Make the selfie appear in the beginning
        s.picture.material.opacity = window.scrollY * 0.005
        s.frame.material.opacity = window.scrollY * 0.005
      }
    })

    // Animate each portrait
    portraits.current.forEach((p, i) => {
      if (p.group) {
        const dist = camera.current.position.z - p.group.position.z
        const distClamped = dist > 2 ? 2 : dist < -2 ? -2 : dist
        const distNormalized = distClamped

        // Show the info layer if the portrait is close enough
        if (distNormalized < minDist && distNormalized > 0.3) {
          showPortrait = portraits.current[i]
        }

        // Make the portrait appear in the beginning
        p.picture.material.opacity = window.scrollY * 0.005
        p.frame.material.opacity = window.scrollY * 0.005
      }
    })

    // The camera follows the scroll
    camera.current.position.z = 2 - window.scrollY * 0.005

    if (showPortrait && !showLayer) {
      console.log('Show the info layer and follow the camera')

      setSelectedPortrait(showPortrait)
      showLayer = true
      setShowInfoLayer(true)

      // tween to best position and follow the camera for a certain amount
      // of scroll. Then tween to the original position
      lastPinedPosition.current = window.scrollY
    } else if (!showPortrait && showLayer) {
      showLayer = false
      setShowInfoLayer(false)
    }

    // The portrait follows the camera for a while if close enough. Otherwise the reader could miss it.
    if (showPortrait) {
      showPortrait.group.position.z = camera.current.position.z - minDist + 0.2

      // Indicate when the portait will stop to follow the camera after a few scroll otherwise
      // the reader will not understand what is happening
      scrollHint.current.style.width =
        (Math.abs(window.scrollY - lastPinedPosition.current) / 1000) * 100 +
        '%'

      // When the user has scroll enough, the portrait goes back to its original position
      // we could have used a tween here
      if (Math.abs(window.scrollY - lastPinedPosition.current) > 1000) {
        console.log('unpin the portrait')

        // Should be tween opacity then go back
        showPortrait.group.position.z = showPortrait.position.z
      }
    }

    frame += 0.01
    renderer.current.render(scene.current, camera.current)
  }

  const init = () => {
    // The loader used to load the images
    const loader = new THREE.TextureLoader()
    // The size of each picture in the scene
    // (heigth is defined by the image aspect)
    const planeWidth = 0.5

    // Create the scene with fog and camera
    scene.current = new THREE.Scene()
    scene.current.fog = new THREE.Fog(0x202346, 0, 16)
    camera.current = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      2000
    )
    camera.current.x = 0
    camera.current.y = 0.1
    camera.current.z = 2

    // Add the camera to the scene
    scene.current.add(camera.current)

    // Create a plane for each selfie
    selfies.current.forEach((s, i) => {
      loader.load(
        require(`../../images/selfies/optimized/selfie_${i + 1}.jpg`),
        map => {
          const group = new THREE.Group()
          const ratio = map.image.height / map.image.width
          const position = {
            x: 1 - Math.random() * 2,
            y: isMobile ? 1 - Math.random() * 2 : 0.5 - Math.random(),
            z: -Math.floor(i / 10) * 9 - (i % 10) * 0.5
          }
          const geometry = {
            picture: new THREE.PlaneGeometry(planeWidth, planeWidth * ratio),
            frame: new THREE.PlaneGeometry(
              planeWidth + planeWidth * 0.2,
              planeWidth * ratio + planeWidth * 0.4
            )
          }
          const material = {
            picture: new THREE.MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              map: map
            }),
            frame: new THREE.MeshBasicMaterial({
              transparent: true,
              color: 0xffffff
            })
          }
          const picture = new THREE.Mesh(geometry.picture, material.picture)
          const frame = new THREE.Mesh(geometry.frame, material.frame)

          // position the picture in the frame
          picture.position.y = planeWidth * 0.1
          picture.position.z = 0.01

          // Position the frame in the scene
          group.position.x = position.x
          group.position.y = position.y
          group.position.z = position.z

          // Hide the picture
          picture.material.opacity = 0
          frame.material.opacity = 0

          // Set the orginal position
          selfies.current[i].position = position

          group.add(frame)
          group.add(picture)

          selfies.current[i].frame = frame
          selfies.current[i].picture = picture
          selfies.current[i].group = group

          // Add the group to the scene
          scene.current.add(group)
        }
      )
    })

    // Create a plane for each portrait
    portraits.current.forEach((p, i) => {
      loader.load(
        require(`../../images/portraits_originaux/${p.pict}.jpg`),
        map => {
          const group = new THREE.Group()
          const ratio = map.image.height / map.image.width
          const position = {
            x: 0,
            y: 0,
            z: (i + 1) * -9 + 3
          }
          const geometry = {
            picture: new THREE.PlaneGeometry(planeWidth, planeWidth * ratio),
            frame: new THREE.PlaneGeometry(
              planeWidth + planeWidth * 0.2,
              planeWidth * ratio + planeWidth * 0.4
            )
          }
          const material = {
            picture: new THREE.MeshBasicMaterial({
              color: 0xffffff,
              transparent: true,
              map: map
            }),
            frame: new THREE.MeshBasicMaterial({
              transparent: true,
              color: 0xffffff
            })
          }
          const picture = new THREE.Mesh(geometry.picture, material.picture)
          const frame = new THREE.Mesh(geometry.frame, material.frame)

          // position the picture in the frame
          picture.position.y = planeWidth * 0.1
          picture.position.z = 0.01

          // position the frame
          group.position.x = position.x
          group.position.y = position.y
          group.position.z = position.z

          // Hide the picture
          picture.material.opacity = 0
          frame.material.opacity = 0

          // Set the original position
          portraits.current[i].position = position

          group.add(frame)
          group.add(picture)

          portraits.current[i].frame = frame
          portraits.current[i].picture = picture
          portraits.current[i].group = group

          // Add the group to the scene
          scene.current.add(group)
        }
      )
    })

    animate()
  }

  const resize = () => {
    console.log('New dimensions')

    if (camera.current) {
      camera.current.aspect = window.innerWidth / window.innerHeight
      camera.current.updateProjectionMatrix()
    }

    renderer.current.setSize(window.innerWidth, window.innerHeight)
    setWindowHeight(window.innerHeight)
  }

  // Initialize threejs when the canvas is ready
  const canvas = useCallback(node => {
    if (node !== null) {
      renderer.current = new THREE.WebGLRenderer({
        logarithmicDepthBuffer: true,
        antialias: true,
        alpha: true,
        canvas: node
      })
      renderer.current.setClearColor(0xffffff, 0)
      init()
    }
  }, [])

  // We need to resize the canvas according to the window
  // when the window is resized
  window.onresize = () => {
    resize()
  }

  useEffect(() => {
    console.log('Mounted')

    // We need to resize the canvas according to the window
    resize()
  }, [])

  return (
    <>
      <div className='canvas-container' style={{ height: 77 / 0.005 }}>
        <div className='canvas-content' style={{ height: windowHeight + 'px' }}>
          <div className='canvas-container-dimmer'>
            <div />
          </div>
          <div
            onClick={() => {
              selectedPortrait && handleClick(selectedPortrait)
            }}
            className={`portrait-info ${showInfoLayer ? 'visible' : ''}`}
          >
            {selectedPortrait && (
              <>
                <div className='shadow' />
                <div className='portrait-quote'>{selectedPortrait.quote}</div>
                <div className='portrait-label'>
                  <div className='scrollHintWrapper'>
                    <div className='scrollHintContainer'>
                      <div className='scrollHint' ref={scrollHint} />
                    </div>
                  </div>
                  <div className='name'>{selectedPortrait.name}</div>
                  <div className='infos'>{selectedPortrait.info}</div>
                  <div className='link'>
                    Lire <i className='ui icon chevron right' />
                  </div>
                </div>
              </>
            )}
          </div>
          <canvas ref={canvas} />
        </div>
      </div>
    </>
  )
}
